/**
 * Floorplan Unit Display Strategies
 * 
 * This module provides modular, extensible strategies for selecting which units
 * to display on floorplan pages based on marketing requirements configured in Contentful.
 * Each strategy determines how many and which units are shown to potential residents.
 */

/**
 * Calculate the age of a vacant unit in days
 * @param {Object} unit - Unit object with moveOutDate
 * @returns {number} Days since move-out, or -1 if not applicable
 */
const getUnitAge = (unit) => {
  if (!unit.isVacant || !unit.moveOutDate) return -1;
  
  const moveOutDate = new Date(unit.moveOutDate);
  const today = new Date();
  const diffTime = Math.abs(today - moveOutDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Sort units by price (lowest first)
 * @param {Array} units - Array of unit objects
 * @returns {Array} Sorted array
 */
const sortByPrice = (units) => {
  return [...units].sort((a, b) => Number(a.marketRent) - Number(b.marketRent));
};

/**
 * Sort units by age (most aged first)
 * @param {Array} units - Array of unit objects  
 * @returns {Array} Sorted array
 */
const sortByAge = (units) => {
  return [...units].sort((a, b) => {
    const ageA = getUnitAge(a);
    const ageB = getUnitAge(b);
    
    // Units without valid ages go to the end
    if (ageA === -1 && ageB === -1) return 0;
    if (ageA === -1) return 1;
    if (ageB === -1) return -1;
    
    // Higher age (more days) comes first
    return ageB - ageA;
  });
};

/**
 * Find units with active deals/specials
 * @param {Array} units - Array of unit objects
 * @param {Array} deals - Array of deal objects
 * @returns {Array} Units that have deals
 */
const findUnitsWithDeals = (units, deals) => {
  if (!deals || deals.length === 0) return [];
  
  return units.filter(unit => {
    const unitTrimmed = unit.unit.trim();
    
    for (let deal of deals) {
      // Normalize deal description
      let normalizedSdesc = deal.sdesc.replace(/ Plus/g, '+');
      if (normalizedSdesc.includes('Parc')) {
        normalizedSdesc = normalizedSdesc.replace('Parc', 'The Conroy');
      }
      
      if (deal.unit === unitTrimmed && normalizedSdesc.includes(unit.floorplanName)) {
        return true;
      }
    }
    return false;
  });
};

/**
 * Strategy: Select lowest priced unit only
 * @param {Array} availableUnits - Available units for the floorplan
 * @param {Array} deals - Active deals
 * @returns {Object} Selection result with units to display
 */
export const selectLowestPricedUnit = (availableUnits, deals = []) => {
  if (!availableUnits || availableUnits.length === 0) {
    return { hasUnits: false, units: [] };
  }
  
  // Prioritize units with deals, then sort by price
  const unitsWithDeals = findUnitsWithDeals(availableUnits, deals);
  const unitsWithoutDeals = availableUnits.filter(
    unit => !unitsWithDeals.includes(unit)
  );
  
  const sortedWithDeals = sortByPrice(unitsWithDeals);
  const sortedWithoutDeals = sortByPrice(unitsWithoutDeals);
  
  const allSorted = [...sortedWithDeals, ...sortedWithoutDeals];
  
  return {
    hasUnits: true,
    units: [allSorted[0]],
    strategy: 'Lowest priced unit'
  };
};

/**
 * Strategy: Select lowest priced unit AND most aged unit
 * @param {Array} availableUnits - Available units for the floorplan
 * @param {Array} deals - Active deals
 * @returns {Object} Selection result with units to display
 */
export const selectLowestPricedAndMostAged = (availableUnits, deals = []) => {
  if (!availableUnits || availableUnits.length === 0) {
    return { hasUnits: false, units: [] };
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log('selectLowestPricedAndMostAged - Available units:', availableUnits.length);
  }
  
  // Get lowest priced unit (with deal priority)
  const lowestPricedResult = selectLowestPricedUnit(availableUnits, deals);
  const lowestPricedUnit = lowestPricedResult.units[0];
  
  if (process.env.NODE_ENV === 'development') {
    console.log('Lowest priced unit:', lowestPricedUnit.unit, 'at $', lowestPricedUnit.marketRent);
  }
  
  // Find most aged unit (must be different from lowest priced)
  const vacantUnits = availableUnits.filter(unit => unit.isVacant && unit.moveOutDate);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('Vacant units with move-out dates:', vacantUnits.length);
  }
  
  const sortedByAge = sortByAge(vacantUnits);
  
  // Find the most aged unit that isn't the lowest priced unit
  let mostAgedUnit = null;
  for (let unit of sortedByAge) {
    const age = getUnitAge(unit);
    if (process.env.NODE_ENV === 'development') {
      console.log('Checking unit:', unit.unit, 'age:', age, 'days');
    }
    if (unit.unit !== lowestPricedUnit.unit) {
      mostAgedUnit = unit;
      break;
    }
  }
  
  // If we found a most aged unit, return both
  if (mostAgedUnit) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Most aged unit found:', mostAgedUnit.unit, 'at $', mostAgedUnit.marketRent);
    }
    return {
      hasUnits: true,
      units: [lowestPricedUnit, mostAgedUnit],
      strategy: 'Lowest priced and most aged'
    };
  }
  
  // Fall back to just lowest priced if no valid aged unit
  if (process.env.NODE_ENV === 'development') {
    console.log('No valid aged unit found, falling back to lowest priced only');
  }
  return {
    hasUnits: true,
    units: [lowestPricedUnit],
    strategy: 'Lowest priced unit',
    fallback: true
  };
};

/**
 * Main selection function - delegates to appropriate strategy
 * @param {string} displayOption - The selected display option from Contentful
 * @param {Array} availableUnits - Available units for the floorplan
 * @param {Array} deals - Active deals
 * @returns {Object} Selection result with units to display
 */
export const selectUnitsForDisplay = (displayOption, availableUnits, deals = []) => {
  // Default to lowest priced if no option specified
  if (!displayOption) {
    displayOption = 'Lowest priced unit';
  }
  
  // Strategy map for easy extension
  const strategies = {
    'Lowest priced unit': selectLowestPricedUnit,
    'Lowest priced and most aged': selectLowestPricedAndMostAged,
    // Future strategies can be added here:
    // 'Featured units': selectFeaturedUnits,
    // 'All units under price': selectAllUnderPrice,
    // 'Newest renovated': selectNewestRenovated,
  };
  
  const strategy = strategies[displayOption];
  
  if (!strategy) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Unknown display option: ${displayOption}, falling back to lowest priced unit`);
    }
    return selectLowestPricedUnit(availableUnits, deals);
  }
  
  return strategy(availableUnits, deals);
};

/**
 * Get display label for a strategy
 * @param {string} displayOption - The display option key
 * @returns {string} Human-readable label
 */
export const getDisplayLabel = (displayOption) => {
  const labels = {
    'Lowest priced unit': 'Lowest Priced Unit',
    'Lowest priced and most aged': 'Featured Units',
    // Add more labels as strategies are added
  };
  
  return labels[displayOption] || 'Available Units';
};